import { Button } from "@/components/ui/button";
import { bannerText, type ViewAs } from "@/lib/viewAs";

/**
 * S7 · The view-as banner (UX v0.3 W5).
 *
 * Deliberately **above** the product chrome and full width, because forgetting
 * the mode is the only real failure here. It renders from `/v1/me`'s `view_as`
 * — the server's own record — so it survives a navigation, a reload and a
 * second tab. There is no client flag that could go missing.
 *
 * It never hides content: an emptied screen would be a worse lie than a marked
 * one. On a narrow viewport the line wraps and the exit stays reachable.
 *
 * Chrome, not a C3 shared component: no module consumes it, and putting it in
 * the module-facing catalogue would make "shell-owned" a statement about the
 * repo rather than about the module contract.
 */
export function ViewAsBanner({ view, onExit }: { view: ViewAs | null; onExit: () => void }) {
  const text = bannerText(view);
  if (!text) return null;

  return (
    <div
      // A live region: entering and leaving view-as is announced, because a
      // mode change nobody is told about is a mode change that gets forgotten
      // (UX §10 announcements).
      role="status"
      aria-live="polite"
      className="flex w-full flex-wrap items-center justify-between gap-3 bg-ks-purple px-4 py-2 text-ks-surface"
    >
      <span className="text-sm font-bold">
        <span aria-hidden="true">👁 </span>
        {text}
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={onExit}
        className="bg-ks-surface text-ks-purple hover:bg-ks-grey-4"
      >
        Exit view-as
      </Button>
    </div>
  );
}
